/*! @azure/msal-browser-1p v2.37.1 2023-06-12 */
'use strict';
import { __assign } from '../_virtual/_tslib.js';
import { PerformanceEvents, AzureCloudInstance } from '@azure/msal-browser';
import { ApplicationInsights } from '@microsoft/1ds-analytics-js';

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
var PerformanceTelemetryReporter = /** @class */ (function () {
    function PerformanceTelemetryReporter(logger, configuration) {
        this.logger = logger;
        this.isSampled = !(configuration === null || configuration === void 0 ? void 0 : configuration.disabled) && this.calculateSampling(configuration === null || configuration === void 0 ? void 0 : configuration.samplingPercentage);
        this.logger.verbose("PerformanceTelemetryReporter: Sampled for 1p telemetry: " + this.isSampled.toString());
        this.eventsToEmit = (configuration === null || configuration === void 0 ? void 0 : configuration.eventsToEmit) || [
            PerformanceEvents.AcquireTokenSilent,
            PerformanceEvents.SsoSilent,
            PerformanceEvents.AcquireTokenByCode
        ];
        this.logger.verbose("PerformanceTelemetryReporter: Events to be tracked include : " + this.eventsToEmit);
        this.logger.verbose("PerformanceTelemetryReporter: Events passed in the PerformanceTelemetryConfiguration are: " + (configuration === null || configuration === void 0 ? void 0 : configuration.eventsToEmit));
        var appInsightsConfig = {
            instrumentationKey: PerformanceTelemetryReporter.instrumentationKey,
            extensions: [],
        };
        this.appInsights = new ApplicationInsights();
        this.appInsights.initialize(appInsightsConfig, []);
        this.logger.verbose("PerformanceTelemetryReporter: Initialized");
    }
    // Function to calculate Sampling rate stochastically 
    PerformanceTelemetryReporter.prototype.calculateSampling = function (configuredSamplingRate) {
        if (configuredSamplingRate === 0) {
            this.logger.verbose(" PerformanceTelemetryReporter: No sampling rate configured.");
            return false;
        }
        this.logger.verbose("PerformanceTelemetryReporter: Configured sampling rate = " + configuredSamplingRate + " ");
        this.logger.verbose("PerformanceTelemetryReporter: DEFAULT sampling rate = " + PerformanceTelemetryReporter.DEFAULT_SAMPLING_RATE);
        var randomNumber = Math.random();
        var samplingRate = configuredSamplingRate || PerformanceTelemetryReporter.DEFAULT_SAMPLING_RATE;
        return randomNumber <= samplingRate;
    };
    PerformanceTelemetryReporter.prototype.sendCustomEvent = function (event) {
        var dataToEmit = __assign(__assign({}, event), { appName: undefined // Do not emit appName until privacy concerns are resolved
         });
        var event1 = {
            name: event.name,
            data: dataToEmit
        };
        this.logger.verbose("PerformanceTelemetryReporter: Sending " + event1.name + " for tracking");
        this.appInsights.track(event1);
    };
    PerformanceTelemetryReporter.prototype.canSendEvent = function (event) {
        var _a;
        // Is the event included in list of events to report on
        var emitEvent = !this.eventsToEmit || ((_a = this.eventsToEmit) === null || _a === void 0 ? void 0 : _a.indexOf(event.name)) > -1;
        // Is the configured authority in the list of allow clouds
        var allowedCloud = PerformanceTelemetryReporter.DEFAULT_ALLOWED_CLOUDS.some(function (cloud) { return event.authority.indexOf(cloud.toString()) === 0; });
        // Event is sendable if it is sampled, is included in the list of eventsToEmit/passed in the configuration, AND belongs to an allowed cloud
        return this.isSampled && emitEvent && allowedCloud;
    };
    PerformanceTelemetryReporter.prototype.getPerformanceTelemetryCallback = function () {
        var _this = this;
        this.logger.trace("PerformanceTelemetryReporter: Attaching callback to send custom metrics.");
        var perfCallback = function (events) {
            events.forEach(function (event) {
                if (_this.canSendEvent(event)) {
                    _this.logger.verbose("PerformanceTelemetryReporter: Emitting telemetry for " + event.name, event.correlationId);
                    _this.sendCustomEvent(event);
                }
                else {
                    _this.logger.verbose("PerformanceTelemetryReporter: Skipping telemetry for " + event.name, event.correlationId);
                }
            });
        };
        this.logger.verbose(" PerformanceTelemetryReporter: Returning callback as telemetry emitted.");
        return perfCallback;
    };
    PerformanceTelemetryReporter.DEFAULT_SAMPLING_RATE = 0.1; // Default rate of 10%
    PerformanceTelemetryReporter.DEFAULT_ALLOWED_CLOUDS = [
        AzureCloudInstance.AzurePublic,
        AzureCloudInstance.AzurePpe // TODO: Here for testing, consider removing
    ];
    PerformanceTelemetryReporter.instrumentationKey = "d634483c08244c1ca09af2b2d952c92e-ab2bba03-2ba3-49d8-a82c-ef6da750d8ab-7725";
    return PerformanceTelemetryReporter;
}());

export { PerformanceTelemetryReporter };
//# sourceMappingURL=PerformanceTelemetryReporter.js.map
